import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { ICapabilities } from 'library/ts/interfaces/utilities/capabilities/ICapabilities';
import { ICapabilitiesVersion } from 'library/ts/interfaces/utilities/capabilities/version/ICapabilitiesVersion';
import { ICapabilitiesVersionEdge } from 'library/ts/interfaces/utilities/capabilities/version/ICapabilitiesVersionEdge';
import { ICapabilitiesVersionIOS } from 'library/ts/interfaces/utilities/capabilities/version/ICapabilitiesVersionIOS';
import { Browser } from 'library/ts/model/enums/system/Browser';
import { IOSDevice } from 'library/ts/model/enums/system/IOSDevice';
import { OperatingSystem } from 'library/ts/model/enums/system/OperatingSystem';
import { VideoType } from 'library/ts/model/enums/system/VideoType';
import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import { Utilities } from 'library/ts/utilities/Utilities';

declare global {
	interface Window {
		DocumentTouch: any;
		chrome: any;
		opera: any;
		opr: any;
		webkitAudioContext: any;
		webkitRequestFileSystem: any;
	}
}

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Capabilities class, detects runtime environment
 */
export class Capabilities implements ICapabilities {
	// storage for Utilities reference
	#utils: Utilities;

	#videoElement: HTMLVideoElement;
	#audioElement: HTMLAudioElement;
	#canvasElement: HTMLCanvasElement;
	#isChrome: boolean;
	#isPrivateMode: boolean = false;
	#os: OperatingSystem = null;
	#osVersion: unknown;
	#browser: Browser;
	#browserVersion: number;
	#videoFormatSupport: { [key in VideoType]: string } = {
		OGG: null,
		H264: null,
		WEBM: null,
		VP9: null,
		HLS: null,
		MPEG4: null
	};

	/**
	 * @description Constructor
	 * @param {Utilities} utilities
	 */
	constructor( utilities: Utilities ) {
		this.#utils = utilities;
		this.#videoElement = document.createElement( 'video' );
		this.#audioElement = new Audio();
		this.#canvasElement = document.createElement( 'canvas' );
		this.#isChrome = (
			!!window.chrome
			&&
			( this.utils.Type.isArray( this.userAgent.match( /Chrom(e|ium)\/(\S+)/ ) ) )
		);
	}

	/**
	 * @description Name of this class
	 */
	get name() {
		return 'Capabilities';
	}

	/**
	 * @description Returns instance of Utilities
	 */
	get utils() {
		return this.#utils;
	}

	/**
	 * @description DB of String values
	 */
	get STRING() {
		return this.utils.Object.enumToObject( OperatingSystem ) as { [key in OperatingSystem]: OperatingSystem; };
	}

	/**
	 * @description User agent string...
	 */
	get userAgent() {
		return navigator.userAgent;
	}

	/**
	 * @description Supported video formats
	 */
	get videoFormatSupport() {
		return this.#videoFormatSupport;
	}

	/**
	 * @description Check codec: h264: avc1.4d001f,mp4a.40.2, avc1.42001e,mp4a.40.2
	 */
	get h264() {
		return '' !== (
			this.#videoElement.canPlayType( 'video/mp4; codecs="avc1.42001e,mp4a.40.2"' )
			&&
			this.#videoElement.canPlayType( 'video/mp4; codecs="avc1.42001e,mp4a.40.2"' )
		);
	}

	/**
	 * @description Check for mpeg4 support
	 */
	get mpeg4() {
		return '' !== this.#videoElement.canPlayType( 'video/mp4; codecs="mp4v.20.8"' );
	}

	/**
	 * @description Check for Ogg support
	 */
	get ogg() {
		return '' !== this.#videoElement.canPlayType( 'video/ogg; codecs="theora"' );
	}

	/**
	 * @description Check for Webm support
	 */
	get webm() {
		return '' !== this.#videoElement.canPlayType( 'video/webm; codecs="vp8, vorbis"' );
	}

	/**
	 * @description Is canvas support available
	 */
	get canvasSupport() {
		return !!this.#canvasElement.getContext; // is IE
	}

	/**
	 * @description Support for Dolby Digital Plus
	 */
	get dolbyDigitalPlus() {
		return this.dolbyDigitalPlusSupport();
	}

	/**
	 * @description Opera 8.0+
	 */
	get isOpera() {
		return Boolean(
			window.opera
			||
			window.opr
			||
			( this.userAgent.indexOf( ' OPR/' ) > -1 )
			||
			( this.userAgent.indexOf( ' Coast/' ) > -1 )
			||
			( this.userAgent.indexOf( ' OPiOS/' ) > -1 )
		);
	}

	/**
	 * @description Check if the Opera browser is Opera Mini or Opera Mobile in regular mode (called High Savings Mode)
	 */
	get isOperaHigh() {
		return (
			( this.userAgent.indexOf( 'OPR/' ) > -1 )
			&&
			( this.userAgent.indexOf( 'Mobile' ) > -1 )
			&&
			( this.userAgent.indexOf( 'Presto/' ) < 0 )
		);
	}

	/**
	 * @description Check if the Opera browser is Opera Mini in Extreme Savings Mode
	 */
	get isOperaExtreme() {
		return (
			( this.userAgent.indexOf( 'Opera Mini/' ) > -1 )
			&&
			( this.userAgent.indexOf( 'Presto/' ) > -1 )
		);
	}

	/**
	 * @description Chrome 1+
	 */
	get isChrome() {
		return this.#isChrome;
	}

	/**
	 * @description Chrome + IOS
	 */
	get isChromeIOS() {
		return /CriOS/i.test( navigator.userAgent ) && /iphone|ipod|ipad/i.test( navigator.userAgent );
	}

	/**
	 * @description Firefox + IOS. Firefox for iOS renders via WebKit (Apple requires this on iOS)
	 * and identifies itself with `FxiOS/` rather than `Firefox/`, so the generic `isFirefox` check
	 * never matches it.
	 */
	get isFirefoxIOS() {
		return /FxiOS\//i.test( this.userAgent ) && /iphone|ipod|ipad/i.test( this.userAgent );
	}

	/**
	 * @description Opera + IOS. Opera for iOS renders via WebKit and identifies itself with
	 * `OPiOS/`. `isOpera` already matches this token, but there is no dedicated version parser for
	 * it (see `getOperaIOSVersion`).
	 */
	get isOperaIOS() {
		return /OPiOS\//i.test( this.userAgent ) && /iphone|ipod|ipad/i.test( this.userAgent );
	}

	/**
	 * @description Edge + IOS. Edge for iOS renders via WebKit and identifies itself with
	 * `EdgiOS/` rather than `Edge/`/`Edg/`, so the generic `isEdge` check never matches it.
	 */
	get isEdgeIOS() {
		return /EdgiOS\//i.test( this.userAgent ) && /iphone|ipod|ipad/i.test( this.userAgent );
	}

	/**
	 * @description Firefox 1.0+
	 */
	get isFirefox() {
		// https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#which_part_of_the_user_agent_contains_the_information_you_are_looking_for
		return this.userAgent.indexOf( 'Firefox/' ) != -1 && this.userAgent.indexOf( 'Seamonkey/' ) === -1;
	}

	/**
	 * @description Is browser window in private mode?
	 */
	get isPrivateMode() {
		return this.#isPrivateMode;
	}

	/**
	 * @description Safari
	 */
	get isSafari() {
		// User Agent type detection
		return /^((?!chrome|android).)*safari/i.test( navigator.userAgent );
	}

	/**
	 * @description Blink
	 */
	get isBlink() {
		return ( this.isChrome || this.isOpera ) && !!window.CSS;
	}

	/**
	 * @description Android
	 */
	get android() {
		return !!( this.userAgent.match( new RegExp( 'android', 'i' ) ) );
	}

	/**
	 * @description Blackberry
	 */
	get bb6() {
		return /BlackBerry.*Version\/6\.0/.test( this.userAgent );
	}

	/**
	 * @description Device pixel ratio
	 */
	get devicePixelRatio() {
		return window.devicePixelRatio || 1;
	}

	/**
	 * @description Screen width
	 */
	get screenWidth() {
		return screen.width * this.devicePixelRatio;
	}

	/**
	 * @description Screen height
	 */
	get screenHeight() {
		return screen.height * this.devicePixelRatio;
	}

	/**
	 * @description OS - set in getOS() method call
	 */
	get os() {
		return this.#os;
	}

	/**
	 * @description OS version - set in getOS() method call
	 */
	get osVersion() {
		return this.#osVersion;
	}

	/**
	 * @description Return user's browser
	 */
	get browser() {
		return this.#browser;
	}

	/**
	 * @description Return user's browser major version
	 */
	get browserVersion() {
		return this.#browserVersion;
	}

	/**
	 * @description Mobile device detection
	 * @see {@link https://jimlovetm.com/0122-what-is-the-best-way-to-detect-a-mobile-device-with-jquery-or-javascript}
	 * @see {@link https://stackoverflow.com/questions/3514784/what-is-the-best-way-to-detect-a-mobile-device-in-jquery}
	 */
	get isMobile() {
		return {
			Android: () => {
				return this.utils.Type.isArray( this.userAgent.match( /Android/i ) );
			},
			BlackBerry: () => {
				return this.utils.Type.isArray( this.userAgent.match( /BlackBerry/i ) );
			},
			iOS: () => {
				return this.utils.Type.isArray( this.userAgent.match( /iPhone|iPad|iPod/i ) );
			},
			Opera: () => {
				return this.utils.Type.isArray( this.userAgent.match( /Opera Mini|Opera Mobi/i ) );
			},
			Windows: () => {
				return this.utils.Type.isArray( this.userAgent.match( /IEMobile/i ) );
			},
			any: () => {
				return (
					this.isMobile.Android() === true
					||
					this.isMobile.BlackBerry() === true
					||
					this.isMobile.iOS() === true
					||
					this.isMobile.Opera() === true
					||
					this.isMobile.Windows() === true
				);
			}
		};
	}

	/**
	 * @description Chrome version
	 */
	get chromeVersion() {
		return this.getChromeVersion();
	}

	/**
	 * @description Chrome + IOS version (`CriOS/` token — `getChromeVersion` only parses `Chrome/`/`Chromium/`)
	 */
	get chromeIOSVersion() {
		return this.getChromeIOSVersion();
	}

	/**
	 * @description Safari version
	 */
	get safariVersion() {
		return this.getSafariVersion();
	}

	/**
	 * @description Indicates if current browser is Internet Explorer
	 */
	get isIE() {
		return ( this.userAgent.indexOf( 'MSIE ' ) > 0 || this.userAgent.indexOf( 'Trident/' ) > 0 );
	}

	/**
	 * @description Internet Explorer version
	 */
	get ieVersion() {
		return this.getIEVersion();
	}

	/**
	 * @description Edge detection based on UA string. Includes `EdgA/` (Edge for Android) alongside
	 * legacy `Edge/` and Chromium `Edg/` — Edge for Android runs the real Chromium engine (unlike
	 * Edge for iOS, which is WebKit-based and detected separately via `isEdgeIOS`), so it belongs
	 * in the same "real Edge" bucket as desktop.
	 */
	get isEdge() {
		return (
			this.userAgent.indexOf( 'Edge/' ) > -1
			|| this.userAgent.indexOf( 'Edg/' ) > -1
			|| this.userAgent.indexOf( 'EdgA/' ) > -1
		);
	}

	/**
	 * @description Edge version
	 */
	get edgeVersion() {
		return this.getEdgeVersion();
	}

	/**
	 * @description Edge + IOS version (`EdgiOS/` token — `getEdgeVersion` only parses `Edge/`/`Edg/`/`EdgA/`)
	 */
	get edgeIOSVersion() {
		return this.getEdgeIOSVersion();
	}

	/**
	 * @description Firefox version
	 */
	get firefoxVersion() {
		return this.getFirefoxVersion();
	}

	/**
	 * @description Firefox + IOS version (`FxiOS/` token — `getFirefoxVersion` only parses `Firefox/`)
	 */
	get firefoxIOSVersion() {
		return this.getFirefoxIOSVersion();
	}

	/**
	 * @description Opera version
	 */
	get operaVersion() {
		return this.getOperaVersion();
	}

	/**
	 * @description Opera + IOS version (`OPiOS/` token — `getOperaVersion` only parses `OPR/`/`Opera`)
	 */
	get operaIOSVersion() {
		return this.getOperaIOSVersion();
	}

	/**
	 * @description Full version string of whatever browser is detected, covering desktop, Android,
	 * and iOS third-party-browser variants. iOS is checked first since every third-party browser
	 * there is WebKit under the hood and its UA contains "Safari" — checking the distinguishing
	 * tokens (CriOS/FxiOS/OPiOS/EdgiOS) before falling back to `isSafari` avoids misattributing them
	 * as Safari. Desktop/Android use their real per-browser tokens directly; `isEdge` is checked
	 * before `isChrome` since Chromium-Edge's UA also contains a literal `Chrome/` token.
	 * This ordering is independent of — and must stay in sync with — the `#isChrome` sanity-check
	 * token list in `detect()` below, which reaches the same Edge/Opera-vs-Chrome result by
	 * neutralizing `isChrome` instead of reordering it.
	 */
	get browserVersionFull(): string | null {
		if ( this.isMobile.iOS() ) {
			if ( this.isChromeIOS ) {
				return this.chromeIOSVersion.version;
			}
			if ( this.isFirefoxIOS ) {
				return this.firefoxIOSVersion.version;
			}
			if ( this.isOperaIOS ) {
				return this.operaIOSVersion.version;
			}
			if ( this.isEdgeIOS ) {
				return this.edgeIOSVersion.version;
			}
			if ( this.isSafari ) {
				return this.safariVersion.version;
			}
			return null;
		}

		if ( this.isEdge ) {
			return this.edgeVersion.full;
		}
		if ( this.isOpera ) {
			return this.operaVersion.version;
		}
		if ( this.isChrome ) {
			return this.chromeVersion.version;
		}
		if ( this.isSafari ) {
			return this.safariVersion.version;
		}
		if ( this.isFirefox ) {
			return this.firefoxVersion.version;
		}
		if ( this.isIE ) {
			return String( this.ieVersion );
		}
		return null;
	}

	/**
	 * @description is desktop?
	 */
	get isDesktop() {
		return !this.isMobile.any();
	}

	/**
	 * @description Are we running on touch enabled device
	 */
	get isTouchDevice() {
		return ( 'ontouchstart' in window || window.DocumentTouch && document instanceof window.DocumentTouch );
	}

	/**
	 * @description Has touch?
	 */
	get hasTouch() {
		return window.matchMedia( '(any-pointer: coarse)' ).matches;
	}

	/**
	 * @description Has mouse?
	 */
	get hasMouse() {
		return window.matchMedia( '(pointer:fine)' ).matches;
	}

	/**
	 * @description Is iOS?
	 */
	get ios() {
		return this.iOS().ios;
	}

	/**
	 * @description iOS version
	 */
	get iosVersion() {
		return this.iOS().version;
	}

	/**
	 * @description Checks support for Dolby Digital Plus audio
	 * @see {@link https://developer.dolby.com/platforms/html5/browser-support/}
	 * @see {@link https://github.com/DolbyDev/Web_Feature_Detection/blob/master/js/dolby.js}
	 */
	dolbyDigitalPlusSupport = () => {
		let supportDDPlus = false;

		// check to see if ec-3 (Dolby Digital Plus) can be played
		if ( this.#audioElement.canPlayType( 'audio/mp4;codecs="ec-3"' ) !== '' ) {

			// iPhone/Pod/Pad iOS 10 on Safari 10 Browser
			if (
				(
					this.userAgent.indexOf( 'CPU iPhone OS 10_0' ) !== -1
					|| this.userAgent.indexOf( 'CPU OS 10_0' ) !== -1
				)
				&& this.userAgent.indexOf( 'Safari' ) !== -1
				&& this.userAgent.indexOf( 'Version/10' ) !== -1
			) {
				supportDDPlus = true;
			}

			// Mac macOS 10_12 Safari 10 Browser
			// NOte: OS X 10_11 (El Capitan) and Safari 9 support DD+ too, except there were some issues so we're leaving that combination out
			if (
				this.userAgent.indexOf( 'Mac OS X 10_12' ) !== -1
				&& this.userAgent.indexOf( 'Safari' ) !== -1
				&& this.userAgent.indexOf( 'Version/10' ) !== -1
			) {
				// everything checks out so we can play Dolby Digital Plus
				supportDDPlus = true;
			}

			// Microsoft Windows 10 EDGE Browser
			if ( this.userAgent.indexOf( 'Edge' ) !== -1 ) {
				supportDDPlus = true;
			}

			// Microsoft Windows 10 Phone, handle this false positive
			if ( this.userAgent.indexOf( 'Windows Phone 10' ) !== -1 ) {
				supportDDPlus = false;
			}

		}

		return supportDDPlus;
	};

	/**
	 * @description iOS detection
	 * NOTE: Both navigator.userAgent and navigator.platform can be faked by the user or a browser extension
	 */
	iOS = () => {
		const iDevices = [
			'iPad Simulator',
			'iPhone Simulator',
			'iPod Simulator',
			'iPad',
			'iPhone',
			'iPod'
		];

		/**
		 * @description Output object
		 * @type {{ios: Boolean, version: Number, device: String}}
		 */
		const out: ICapabilitiesVersionIOS = {
			ios: false,
			version: null,
			device: null
		};

		while ( iDevices.length ) {
			let device = iDevices.pop();
			if ( this.userAgent.indexOf( device ) > -1 ) { // we got iDevice
				// sanity check for string typing later
				if ( device === 'iPad' ) {
					device = IOSDevice.IPAD;
				}
				if ( device === 'iPhone' ) {
					device = IOSDevice.IPHONE;
				}

				out.ios = true;
				out.device = device;
				// set version
				out.version = parseFloat(
					( '' + ( /CPU.*OS ([0-9_]{1,5})|(CPU like).*AppleWebKit.*Mobile/i.exec( this.userAgent ) || [ 0, '' ] )[ 1 ] )
						.replace( TrueType.UNDEFINED, '3_2' ).replace( '_', '.' ).replace( '_', '' )
				) || null;
				return out;
			}
		}

		return out;
	};

	/**
	 * @description Returns whether userAgent is iOS device
	 * @returns {boolean}
	 */
	isIOS = (): boolean => {
		return ( /iP(hone|od|ad)/.test( this.userAgent ) );
	};

	/**
	 * @description Returns iOS version
	 * @returns {number|boolean}
	 */
	iOSVersion = (): number | boolean => {
		if ( ( <any>window ).MSStream ) {
			// There is some iOS in Windows Phone...
			// https://msdn.microsoft.com/en-us/library/hh869301(v=vs.85).aspx
			return false;
		}
		const match: any = ( this.userAgent.match( /\b[0-9]+_[0-9]+(?:_[0-9]+)?\b/ ) || [ '' ] )[ 0 ].split( '_' );
		let version;

		if ( this.isIOS() && !this.utils.Type.isNullOrUndefined( match ) ) {
			version = [
				parseInt( match[ 0 ], 10 ),
				parseInt( match[ 1 ], 10 ),
				parseInt( match[ 2 ] || 0, 10 )
			];
			return parseFloat( version.join( '.' ) );
		}
		// nothing detected
		return false;
	};

	/**
	 * @description Returns Android version
	 */
	getAndroidVersion = () => {
		const match = this.userAgent.toLowerCase().match( /android\s([0-9\.]*)/ );
		return match ? match[ 1 ] : false;
	};

	/**
	 *  @description OS detection
	 * @returns {Promise<IPromiseResolveGeneric>}
	 */
	#getOS = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {

				let operatingSystem: OperatingSystem = null;
				let osVersion;

				if ( /Mac/.test( this.userAgent ) === true ) {
					operatingSystem = OperatingSystem.MAC_OS; // OSX detected by using userAgent

					// INFO check OS against browser simulator (using userAgent string)
					if ( this.utils.Type.isArray( this.userAgent.match( /iPhone|iPad|iPod/i ) ) === true ) { // iOS
						operatingSystem = OperatingSystem.IOS;
						osVersion = this.iOSVersion();
					} else { // OSX detected by using userAgent
						// check if we have android userAgent string (sanity check for simulated user agent string)
						if ( /Android/.test( this.userAgent ) ) {
							operatingSystem = OperatingSystem.ANDROID;
							osVersion = this.getAndroidVersion();
						}

						// sanity check on OSX desktop
						if ( /Mac OS X/.test( this.userAgent ) ) {
							// base version to start with
							const baseOSXVersion = 9;
							// start incrementing version
							for ( let i = 0; i < 30; i++ ) {
								const dynamicOSXVersion = ( baseOSXVersion + i );
								// create dynamic RegEx for OSX version
								const osxVersionRegEx = new RegExp( 'Mac OS X (' + dynamicOSXVersion + '[\\.\\_\\d]+)' );
								// Execute RegEx against userAgent string
								const osxVersionArray = osxVersionRegEx.exec( this.userAgent );
								// If result is not null
								if ( this.utils.Type.isNull( osxVersionArray ) === false ) {
									// and is a non-empty Array
									if (
										this.utils.Type.isArray( osxVersionArray )
										&&
										osxVersionArray.length > 1
										&&
										this.utils.Type.isString( osxVersionArray[ 1 ] )
									) {
										osVersion = osxVersionArray[ 1 ].split( '_' ).join( '.' );
									}
									break; // exit loop
								}
							}
						}
					}
				} else if ( /Windows/.test( this.userAgent ) ) {
					operatingSystem = OperatingSystem.WINDOWS;

					const regWindows = /Windows\ NT\ ([^\s]+)[;\)]/;
					const re = new RegExp( regWindows );
					const test = re.exec( this.userAgent );
					if ( this.utils.Type.isArray( test ) ) {
						const winVersionNumber = test[ 1 ];

						switch ( winVersionNumber ) {
							case '5.1':
								operatingSystem = OperatingSystem.WINDOWS_XP;
								osVersion = 'XP';
								break;
							case '6.0':
								operatingSystem = OperatingSystem.WINDOWS_VISTA;
								osVersion = 'Vista';
								break;
							case '6.1':
								operatingSystem = OperatingSystem.WINDOWS_7;
								osVersion = '7';
								break;
							case '6.2':
								operatingSystem = OperatingSystem.WINDOWS_8;
								osVersion = '8';
								break;
							case '6.3':
								operatingSystem = OperatingSystem.WINDOWS_8_1;
								osVersion = '8.1';
								break;
							case '10.0':
								operatingSystem = OperatingSystem.WINDOWS_10;
								osVersion = '10';
								break;
							case '11.0':
							case '12.0':
							case '13.0':
								operatingSystem = OperatingSystem.WINDOWS_11;
								osVersion = '11';
								break;
						}
					}

					/**
					 * @description Windows detection per Microsoft
					 * @see {@link https://docs.microsoft.com/en-us/microsoft-edge/web-platform/how-to-detect-win11}
					 */
					if (
						this.utils.Type.isDefined( navigator[ 'userAgentData' ] )
						&&
						this.utils.Type.isDefined( navigator[ 'userAgentData' ].getHighEntropyValues )
					) {
						navigator[ 'userAgentData' ].getHighEntropyValues( [ 'platformVersion' ] )
							.then( ( ua ) => {
								if ( navigator[ 'userAgentData' ].platform === 'Windows' ) {
									const majorPlatformVersion = parseInt( ua.platformVersion.split( '.' )[ 0 ] );
									if ( majorPlatformVersion >= 13 ) {
										// Windows 11 or later
										operatingSystem = OperatingSystem.WINDOWS_11;
										osVersion = '11';
										this.#os = operatingSystem;
										this.#osVersion = osVersion;
										resolve( { msg: 'done', success: true, result: operatingSystem } );
									} else if ( majorPlatformVersion > 0 ) {
										// Windows 10
									} else {
										// Before Windows 10
									}
								} else {
									// Not running on Windows
								}
							} );
					}

				} else if ( /Android/.test( this.userAgent ) ) {
					operatingSystem = OperatingSystem.ANDROID;
					osVersion = this.getAndroidVersion();
				} else if ( !operatingSystem && /Linux/.test( this.userAgent ) ) {
					operatingSystem = OperatingSystem.LINUX;
				}

				this.#os = operatingSystem;
				this.#osVersion = osVersion;

				resolve( { msg: 'done', success: true, result: operatingSystem } );
			}
		);
	};

	/**
	 * @description Returns Opera version
	 */
	getOperaVersion = () => {
		const output: ICapabilitiesVersion = {
			version: null,
			majorVersion: null
		};

		if ( this.isOpera === true ) {
			const browser = /Opera|OPR\/(\S+)/.exec( this.userAgent );
			if ( this.utils.Type.isArray( browser ) && browser.length > 1 ) {
				output.version = browser[ 1 ];
				if ( output.version.indexOf( '.' ) > -1 ) {
					const versionArray = output.version.split( '.' );
					if ( this.utils.Type.isArray( versionArray ) && versionArray.length > 0 ) {
						output.majorVersion = parseInt( versionArray[ 0 ] );
					}
				} else {
					output.majorVersion = output.version as any;
				}
			}
		}

		return output;
	};

	/**
	 * @description Returns Opera + IOS version (`OPiOS/` token). `isOpera` already detects this
	 * token, but `getOperaVersion`'s regex has no capture group for it, so it never parses a version
	 */
	getOperaIOSVersion = (): ICapabilitiesVersion => {
		const output: ICapabilitiesVersion = {
			version: null,
			majorVersion: null
		};

		if ( this.isOperaIOS === true ) {
			const rawVersion = this.userAgent.match( /OPiOS\/(\S+)/ );
			if ( this.utils.Type.isArray( rawVersion ) && rawVersion.length > 1 ) {
				output.version = rawVersion[ 1 ];
				output.majorVersion = parseFloat( rawVersion[ 1 ] );
			}
		}

		return output;
	};

	/**
	 * @description Returns Firefox version
	 * @return {{version: String, majorVersion: Number}}
	 * @example
	 * ```typescript
	 * {
	 *      version: '48.0',
	 *      majorVersion: 48
	 * }
	 * ```
	 */
	getFirefoxVersion = () => {
		if ( this.isFirefox === false ) {
			return null;
		}

		return {
			version: this.userAgent.split( 'Firefox/' )[ 1 ],
			majorVersion: parseFloat( this.userAgent.split( 'Firefox/' )[ 1 ] )
		};
	};

	/**
	 * @description Returns Firefox + IOS version (`FxiOS/` token)
	 */
	getFirefoxIOSVersion = (): ICapabilitiesVersion => {
		const output: ICapabilitiesVersion = {
			version: null,
			majorVersion: null
		};

		if ( this.isFirefoxIOS === true ) {
			const rawVersion = this.userAgent.match( /FxiOS\/(\S+)/ );
			if ( this.utils.Type.isArray( rawVersion ) && rawVersion.length > 1 ) {
				output.version = rawVersion[ 1 ];
				output.majorVersion = parseFloat( rawVersion[ 1 ] );
			}
		}

		return output;
	};

	/**
	 * @description Returns Safari version
	 */
	getSafariVersion = () => {
		const output = {
			version: null,
			majorVersion: null
		};

		if ( this.isSafari === true ) {
			const browser = /Version\/(\S+).*Safari\//.exec( this.userAgent );
			if ( this.utils.Type.isArray( browser ) && browser.length > 1 ) {
				output.version = browser[ 1 ];
				if ( output.version.indexOf( '.' ) > -1 ) {
					const versionArray = output.version.split( '.' );
					if ( this.utils.Type.isArray( versionArray ) && versionArray.length > 0 ) {
						output.majorVersion = parseInt( versionArray[ 0 ] );
					}
				} else {
					output.majorVersion = parseInt( output.version );
				}
			}
		}

		return output;
	};

	/**
	 * @description Returns IE version. If > 11 it is Edge
	 */
	getIEVersion = () => {
		const msie = this.userAgent.indexOf( 'MSIE ' );
		if ( msie > 0 ) {
			// IE 10 or older => return version number
			return parseInt( this.userAgent.substring( msie + 5, this.userAgent.indexOf( '.', msie ) ), 10 );
		}

		const trident = this.userAgent.indexOf( 'Trident/' );
		if ( trident > 0 ) {
			// IE 11 => return version number
			const rv = this.userAgent.indexOf( 'rv:' );
			return parseInt( this.userAgent.substring( rv + 3, this.userAgent.indexOf( '.', rv ) ), 10 );
		}

		return null;
	};

	/**
	 * @description Returns Edge browser version. Recognizes legacy `Edge/`, Chromium `Edg/`, and
	 * Android `EdgA/` — the latter two are both the real Chromium engine, so both report
	 * `chromium: true` (Edge for iOS is WebKit-based and handled separately by `getEdgeIOSVersion`)
	 */
	getEdgeVersion = () => {
		const output: ICapabilitiesVersionEdge = {
			majorVersion: null,
			full: null,
			chromium: false
		};

		const edge = this.isEdge;

		if ( <any>edge > 0 ) {
			let edgeToken = 'Edge/';
			if ( this.userAgent.indexOf( edgeToken ) === -1 ) {
				edgeToken = 'Edg/';
			}
			if ( this.userAgent.indexOf( edgeToken ) === -1 ) {
				edgeToken = 'EdgA/';
			}

			let fullVersion = null;
			const temp = this.userAgent.split( edgeToken );
			if ( temp.length > 0 ) {
				fullVersion = temp[ 1 ];
			}

			output.majorVersion = parseInt( fullVersion );
			output.full = fullVersion;
			output.chromium = ( edgeToken === 'Edg/' || edgeToken === 'EdgA/' );
		}

		return output;
	};

	/**
	 * @description Returns Edge + IOS version (`EdgiOS/` token)
	 */
	getEdgeIOSVersion = (): ICapabilitiesVersion => {
		const output: ICapabilitiesVersion = {
			version: null,
			majorVersion: null
		};

		if ( this.isEdgeIOS === true ) {
			const rawVersion = this.userAgent.match( /EdgiOS\/(\S+)/ );
			if ( this.utils.Type.isArray( rawVersion ) && rawVersion.length > 1 ) {
				output.version = rawVersion[ 1 ];
				output.majorVersion = parseFloat( rawVersion[ 1 ] );
			}
		}

		return output;
	};

	/**
	 * @description Returns Chrome version
	 */
	getChromeVersion = () => {
		if ( this.isChrome === false ) {
			return null;
		}
		const rawVersion = this.userAgent.match( /Chrom(e|ium)\/(\S+)/ ); // you will get: Chrome/58.0.3029.110
		let fullVersion;
		if ( this.utils.Type.isArray( rawVersion ) ) {
			fullVersion = rawVersion[ 0 ].split( '/' )[ 1 ]; // you will get: 58.0.3029.110
		}
		return {
			version: fullVersion,
			majorVersion: parseFloat( fullVersion )
		};
	};

	/**
	 * @description Returns Chrome + IOS version (`CriOS/` token)
	 */
	getChromeIOSVersion = (): ICapabilitiesVersion => {
		const output: ICapabilitiesVersion = {
			version: null,
			majorVersion: null
		};

		if ( this.isChromeIOS === true ) {
			const rawVersion = this.userAgent.match( /CriOS\/(\S+)/ );
			if ( this.utils.Type.isArray( rawVersion ) && rawVersion.length > 1 ) {
				output.version = rawVersion[ 1 ];
				output.majorVersion = parseFloat( rawVersion[ 1 ] );
			}
		}

		return output;
	};

	/**
	 * @description Checks if browser is in private mode
	 * @see https://gist.github.com/jherax/a81c8c132d09cc354a0e2cb911841ff1
	 * @param callback {Function}
	 * @return {*}
	 */
	#testPrivateMode = ( callback: ( state: boolean ) => void ): any => {
		const on = () => callback( true ); // is in private mode
		const off = () => callback( false ); // not private mode

		const testLocalStorage = () => {
			try {
				if ( localStorage.length ) {
					off();
				} else {
					localStorage.x = 1;
					localStorage.removeItem( 'x' );
					off();
				}
			} catch ( e ) {
				// Safari only enables cookie in private mode
				// if cookie is disabled then all client side storage is disabled
				// if all client side storage is disabled, then there is no point
				// in using private mode
				navigator.cookieEnabled ? on() : off();
			}
		};

		// Chrome & Opera
		if ( window.webkitRequestFileSystem ) {
			return void window.webkitRequestFileSystem( 0, 0, off, on );
		}

		// Firefox
		if ( 'MozAppearance' in document.documentElement.style ) {
			const db = indexedDB.open( 'test' );
			db.onerror = on;
			db.onsuccess = off;
			return void 0;
		}

		// Safari
		if ( /constructor/i.test( ( <any>window ).HTMLElement ) ) {
			return testLocalStorage();
		}

		// IE10+ & Edge
		if ( !window.indexedDB && ( window.PointerEvent || window[ 'MSPointerEvent' ] ) ) {
			return on();
		}

		// others
		return off();
	};

	/**
	 * @description Start capabilities detection
	 */
	detect = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				// Process video format support detection

				const supportsVideoType = ( type: string ) => {
					// Allow user to create shortcuts, i.e. just "webm"
					const formats: { [key in VideoType]: string; } = {
						OGG: 'video/ogg; codecs="theora"',
						H264: 'video/mp4; codecs="avc1.42E01E"',
						WEBM: 'video/webm; codecs="vp8, vorbis"',
						VP9: 'video/webm; codecs="vp9"',
						HLS: 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"',
						MPEG4: 'video/mp4; codecs="mp4v.20.8"'
					};
					const typeToCheck = formats[ type ] || type;
					return this.#videoElement.canPlayType( typeToCheck );
				};

				for ( const videoTypeKey in VideoType ) {
					this.#videoFormatSupport[ videoTypeKey ] = supportsVideoType( VideoType[ videoTypeKey ] );
				}

				// IMPORTANT: sanity check for Chromium-derivative browsers that also expose window.chrome
				// and stamp a literal "Chrome/" token in their UA (e.g. "Mozilla/5.0 (Windows NT 10.0; Win64; x64)
				// AppleWebKit/537.36 (KHTML, like Gecko) Chrome/64.0.3282.140 Safari/537.36 Edge/18.17763") —
				// Edge (legacy/desktop/Android) is not "real" Chrome and must not be misattributed as Chrome
				// downstream. Desktop Opera (OPR/) is deliberately NOT included here — TopMenuManager's
				// Chromecast-button gate only re-includes Chromium-Edge as an isChrome alternative, so flipping
				// isChrome to false for Opera would silently remove its Cast button. Opera's browser/browserVersion
				// attribution below is instead fixed by checking isOpera before isChrome, leaving isChrome true.
				// This Edge token list must stay in sync with the isEdge getter's (above) — browserVersionFull
				// orders isEdge before isChrome directly rather than relying on this flag, so a mismatch here
				// would silently desync browser/browserVersion from browserVersionFull/deviceVersion again.
				if (
					this.userAgent.indexOf( 'Edge/' ) > -1
					|| this.userAgent.indexOf( 'Edg/' ) > -1
					|| this.userAgent.indexOf( 'EdgA/' ) > -1
					|| this.userAgent.indexOf( 'OPiOS/' ) > -1
				) {
					this.#isChrome = false;
				}

				// run OS detection method
				this.#getOS().then(
					( data ) => {

						// init browser, browserVersion
						// iOS third-party browsers are WebKit under the hood and match isSafari's generic
						// UA check, so their distinguishing tokens must be checked first (mirrors browserVersionFull).
						// The Browser enum has no separate iOS variants, so these map to the same enum values as
						// their desktop/Android counterparts — only the version source differs. isOpera is checked
						// before isChrome since desktop/Android Opera's UA also contains a literal "Chrome/" token
						// (isChrome stays true regardless, per the sanity-check note above, to preserve the Cast gate).
						switch ( true ) {
							case this.isChromeIOS:
								this.#browser = Browser.CHROME;
								this.#browserVersion = this.chromeIOSVersion.majorVersion;
								break;
							case this.isFirefoxIOS:
								this.#browser = Browser.FIREFOX;
								this.#browserVersion = this.firefoxIOSVersion.majorVersion;
								break;
							case this.isOperaIOS:
								this.#browser = Browser.OPERA;
								this.#browserVersion = this.operaIOSVersion.majorVersion;
								break;
							case this.isEdgeIOS:
								this.#browser = Browser.EDGE;
								this.#browserVersion = this.edgeIOSVersion.majorVersion;
								break;
							case this.isOpera:
								this.#browser = Browser.OPERA;
								this.#browserVersion = this.operaVersion.majorVersion;
								break;
							case this.isChrome:
								this.#browser = Browser.CHROME;
								this.#browserVersion = this.chromeVersion.majorVersion;
								break;
							case this.isFirefox:
								this.#browser = Browser.FIREFOX;
								this.#browserVersion = this.firefoxVersion.majorVersion;
								break;
							case this.isSafari:
								this.#browser = Browser.SAFARI;
								this.#browserVersion = this.safariVersion.majorVersion;
								break;
							case this.isEdge:
								this.#browser = Browser.EDGE;
								this.#browserVersion = this.edgeVersion.majorVersion;
								break;
							case this.isIE:
								this.#browser = Browser.IE;
								this.#browserVersion = this.ieVersion;
								break;
						}

						// Run private browser mode detection
						this.#testPrivateMode(
							( is_private ) => {
								// store private mode detection result
								this.#isPrivateMode = is_private;
								// resolve promise
								resolve( { msg: 'done', success: true, result: this } );
							}
						);
					}
				);
			}
		);
	};
}